/**
 * Quest prerequisite chains from `requiredQuests` (plan 10 §2.1, decision 4).
 *
 * A quest may need several earlier quests ("do X and Y first"), so the
 * prerequisites form a DAG: a quest's path is every ancestor in dependency
 * order, and the reverse index answers "what does finishing this unlock?".
 * Pure reads of the bundled QUEST_DATABASE: nothing here makes a quest
 * acceptable; the server keeps that authority (through
 * {@link missingQuestPrerequisites}, the one gate check every surface shares).
 */
import { QUEST_DATABASE, type QuestDefinition } from '../content/generated/quests.gen';

type QuestTable = Record<string, Pick<QuestDefinition, 'id' | 'requiredQuests'>>;

/** The quests that must be completed before `quest` can be offered, in authored order. */
export function questPrerequisites(quest: Pick<QuestDefinition, 'requiredQuests'> | null | undefined): readonly string[] {
  return quest?.requiredQuests ?? [];
}

/** Prerequisites of `quest` that `isCompleted` does not report as done (empty = unlocked). */
export function missingQuestPrerequisites(
  quest: Pick<QuestDefinition, 'requiredQuests'> | null | undefined,
  isCompleted: (questId: string) => boolean,
): string[] {
  return questPrerequisites(quest).filter(id => !isCompleted(id));
}

const unlockIndexCache = new WeakMap<QuestTable, Map<string, string[]>>();

/** Prerequisite → the quests it directly unlocks (one of their requirements), in table order. */
export function questUnlockIndex(table: QuestTable = QUEST_DATABASE): Map<string, string[]> {
  let index = unlockIndexCache.get(table);
  if (!index) {
    index = new Map();
    for (const quest of Object.values(table)) {
      for (const prerequisite of questPrerequisites(quest)) {
        const list = index.get(prerequisite) ?? [];
        list.push(quest.id);
        index.set(prerequisite, list);
      }
    }
    unlockIndexCache.set(table, index);
  }
  return index;
}

/** Quests that list `questId` among their prerequisites. */
export function questsUnlockedBy(questId: string, table: QuestTable = QUEST_DATABASE): string[] {
  return questUnlockIndex(table).get(questId) ?? [];
}

/**
 * Every quest on the way to `questId`, in dependency order (each one after all
 * of its own prerequisites; branches in authored order), `questId` last.
 * Shared ancestors appear once. Stops at an unknown id and guards against an
 * accidental cycle (the compiler rejects cycles; hand-built tables may not).
 */
export function questPrerequisiteChain(questId: string, table: QuestTable = QUEST_DATABASE): string[] {
  const chain: string[] = [];
  const placed = new Set<string>();
  const visiting = new Set<string>();
  const visit = (id: string): void => {
    if (!table[id] || placed.has(id) || visiting.has(id)) return;
    visiting.add(id);
    for (const prerequisite of questPrerequisites(table[id])) visit(prerequisite);
    visiting.delete(id);
    placed.add(id);
    chain.push(id);
  };
  visit(questId);
  return chain;
}

export type QuestStepState = 'done' | 'active' | 'next' | 'locked';

/**
 * Where the player stands on each step of a path: done, in progress, next
 * (every prerequisite done, so it can be taken now; parallel branches can
 * both be next) or locked (something before it is still open).
 */
export function questPathStates(
  chain: readonly string[], completed: ReadonlySet<string>, active: ReadonlySet<string>,
  table: QuestTable = QUEST_DATABASE,
): Array<{ id: string; state: QuestStepState }> {
  return chain.map(id => {
    if (completed.has(id)) return { id, state: 'done' as const };
    if (active.has(id)) return { id, state: 'active' as const };
    const open = missingQuestPrerequisites(table[id], prerequisite => completed.has(prerequisite));
    return { id, state: open.length === 0 ? 'next' as const : 'locked' as const };
  });
}
